import Section from "../components/Section";
import { motion } from "framer-motion";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

export default function AboutGenetics() {
  return (
    <>
      {/* 1) How inheritance works */}
      <Section eyebrow="Genetics" title="How is Thalassemia inherited?">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          {/* Visual explainer with image */}
          <motion.div
            variants={item}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="rounded-xl border bg-white p-6 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <i className="fa-solid fa-dna text-rose-600 text-2xl" />
              <h3 className="font-semibold text-slate-900">Genes from both parents</h3>
            </div>
            {/* <div className="h-32 w-full overflow-hidden rounded-lg border bg-slate-50 mb-4">
              <img src="/images/genetics/dna-hero.svg" alt="" className="h-full w-full object-cover" />
            </div> */}
            <p className="text-slate-600">
              Thalassemia is passed down through genes. If both parents carry a thalassemia trait (minor),
              each pregnancy has fixed probabilities for outcomes.
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              <LegendChip color="ok" label="Normal" />
              <LegendChip color="warn" label="Carrier (Trait)" />
              <LegendChip color="danger" label="Thalassemia Major" />
            </div>
          </motion.div>

          {/* Quick bullets */}
          <motion.ul
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="space-y-3 text-slate-700"
          >
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-circle-check text-emerald-600 mt-1" />
              <span>It’s inherited, not caused by lifestyle, and not contagious.</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-people-group text-rose-600 mt-1" />
              <span>Risk depends on both parents’ carrier status.</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-user-doctor text-sky-600 mt-1" />
              <span>Genetic counseling helps families understand options and plan.</span>
            </li>
          </motion.ul>
        </div>
      </Section>

      {/* 2) Risk scenarios */}
      <Section eyebrow="Risk Scenarios" title="Common inheritance combinations">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid md:grid-cols-3 gap-6"
        >
          <CaseCard
            variants={item}
            title="Minor + Minor"
            items={[
              { text: "25% Major", tone: "danger" },
              { text: "50% Carrier", tone: "warn" },
              { text: "25% Normal", tone: "ok" },
            ]}
            // img="/images/genetics/punnett.png"
          />
          <CaseCard
            variants={item}
            title="Minor + Normal"
            items={[
              { text: "50% Carrier", tone: "warn" },
              { text: "50% Normal", tone: "ok" },
            ]}
          />
          <CaseCard
            variants={item}
            title="Normal + Normal"
            items={[{ text: "100% Normal", tone: "ok" }]}
          />
        </motion.div>
      </Section>

      {/* 3) If both carriers */}
      <Section eyebrow="If Both Are Carriers" title="What should a couple do?">
        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div
            variants={item}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="rounded-xl border bg-white p-6 shadow-sm"
          >
            <div className="grid sm:grid-cols-3 gap-3">
              <ActionChip icon="fa-user-doctor" label="Genetic counseling" />
              <ActionChip icon="fa-file-medical" label="Discuss screening options" />
              <ActionChip icon="fa-people-group" label="Plan pregnancy choices" />
            </div>
            <p className="text-slate-600 mt-4">
              A counselor explains probabilities, available tests, and helps you make informed choices.
            </p>
          </motion.div>

          <motion.div
            variants={item}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="rounded-xl border bg-white p-6 shadow-sm"
          >
            <h4 className="font-semibold text-slate-900 mb-3">FAQs</h4>
            <Accordion
              items={[
                { q: "Does lifestyle change genes?", a: "No. Genes are inherited from parents." },
                { q: "If both are carriers, are all kids affected?", a: "No. Each pregnancy has separate probabilities." },
                { q: "Can carriers live normal lives?", a: "Yes. Most carriers are healthy." },
              ]}
            />
          </motion.div>
        </div>
      </Section>
    </>
  );
}

function LegendChip({ color = "ok", label }) {
  const map = {
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return <div className={`text-xs px-2 py-1 rounded border inline-block ${map[color]}`}>{label}</div>;
}

function CaseCard({ title, items, img, variants }) {
  return (
    <motion.div variants={variants} whileHover={{ y: -3 }} className="rounded-xl border p-5 bg-white shadow-sm">
      <div className="text-xs font-semibold tracking-wide text-rose-600">Genetic Case</div>
      <h4 className="font-semibold text-slate-900">{title}</h4>
      {img && (
        <div className="h-24 w-full overflow-hidden rounded-lg border bg-slate-50 my-3">
          <img src={img} alt="" className="h-full w-full object-cover" />
        </div>
      )}
      <p className="text-sm text-slate-600">Outcomes:</p>
      <div className={`mt-3 grid grid-cols-${items.length > 2 ? "3" : items.length} gap-2 text-center`}>
        {items.map((x) => (
          <LegendChip key={x.text} color={x.tone} label={x.text} />
        ))}
      </div>
    </motion.div>
  );
}

function ActionChip({ icon, label }) {
  return (
    <div className="flex items-center gap-2 text-sm px-3 py-2 rounded border bg-slate-50">
      <i className={`fa-solid ${icon} text-slate-700`} />
      <span className="text-slate-700">{label}</span>
    </div>
  );
}
function Accordion({ items }) {
  return (
    <div className="divide-y">
      {items.map(({ q, a }) => (
        <details key={q} className="py-3 group">
          <summary className="flex cursor-pointer items-center justify-between">
            <span className="font-medium text-slate-800">{q}</span>
            <i className="fa-solid fa-chevron-down text-slate-400 group-open:rotate-180 transition" />
          </summary>
          <p className="text-slate-600 mt-2">{a}</p>
        </details>
      ))}
    </div>
  );
}
